import { useEffect, useRef, type ReactNode } from 'react'
import Swiper from 'swiper'
import { A11y, Autoplay, Navigation, Pagination, Thumbs } from 'swiper/modules'
import type { SwiperOptions } from 'swiper/types'

export interface SliderProps {
  /** Classes placed BEFORE `swiper` on the root, e.g. `ul-services-slider`. */
  className?: string
  /**
   * Classes placed AFTER `swiper`, e.g. `overflow-visible` on the home services
   * slider. Order is preserved because the DOM fingerprint compares class lists and
   * order is significant, e.g. `class="ul-services-slider swiper overflow-visible"`.
   */
  trailingClassName?: string
  /** Swiper options for this instance. */
  options: SwiperOptions
  /** One child per slide; each is wrapped in `.swiper-slide`. */
  slides: ReactNode[]
  /** Rendered inside the `.swiper` root after the wrapper — nav buttons, pagination. */
  children?: ReactNode
  /** Receives the Swiper instance, for the banner's thumb/main pairing. */
  onSwiper?: (swiper: Swiper) => void
}

/**
 * Thin wrapper around Swiper core.
 *
 * Deliberately not `swiper/react`'s `<Swiper>/<SwiperSlide>`: those render their own
 * element structure, and the markup order matters here — `.swiper` wraps
 * `.swiper-wrapper` plus sibling nav elements that live *inside* the root. Driving
 * Swiper imperatively against markup we control keeps the DOM byte-identical to the
 * markup under our control while still using the maintained library.
 *
 * Modules are registered explicitly rather than via the bundle so only what the
 * is actually used is pulled in: autoplay, navigation, pagination, thumbs — and a11y,
 * which emits the `.swiper-notification` live region that screen readers rely on. Do not
 * drop a11y.
 *
 * Registering a module costs nothing at the call sites that do not use it: Swiper only
 * activates one when the corresponding option is passed. `Pagination` was added on
 * 2026-09-03 for Partners' promo slider, whose dots are the only affordance saying
 * there is more than one slide.
 */
export function Slider({ className, trailingClassName, options, slides, children, onSwiper }: SliderProps) {
  const ref = useRef<HTMLDivElement>(null)
  const instance = useRef<Swiper | null>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const swiper = new Swiper(el, { modules: [A11y, Autoplay, Navigation, Pagination, Thumbs], ...options })
    instance.current = swiper
    onSwiper?.(swiper)
    return () => {
      swiper.destroy(true, true)
      instance.current = null
    }
    // Options are static per call site, so this intentionally initialises once rather
    // than tearing down and rebuilding on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div ref={ref} className={[className, 'swiper', trailingClassName].filter(Boolean).join(' ')}>
      <div className="swiper-wrapper">
        {slides.map((slide, i) => (
          <div className="swiper-slide" key={i}>
            {slide}
          </div>
        ))}
      </div>
      {children}
    </div>
  )
}

export interface SliderNavProps {
  /** e.g. `ul-project-details-slider-nav` */
  className: string
  /** Extra classes such as `position-static`. */
  extraClassName?: string
  /**
   * Whether to add the shared `ul-slider-nav` base class.
   *
   * Not every nav uses it: `.ul-project-details-slider-nav` and
   * `.ul-services-slider-nav` do, but `.ul-testimonial-2-slider-nav` is styled entirely
   * on its own. Defaulting to `true` and opting out keeps the common case terse without
   * silently adding a class that would restyle a nav.
   */
  baseClass?: boolean
}

/** The prev/next button pair. */
export function SliderNav({ className, extraClassName, baseClass = true }: SliderNavProps) {
  const classes = [className, baseClass ? 'ul-slider-nav' : null, extraClassName].filter(Boolean).join(' ')
  return (
    <div className={classes}>
      <button className="prev" aria-label="Previous">
        <i className="flaticon-back" />
      </button>
      <button className="next" aria-label="Next">
        <i className="flaticon-next" />
      </button>
    </div>
  )
}
